import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SignupResponse } from '@/api';
import { useNullableTokens } from '@/common/providers/tokens-provider';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, gtmPush, isApiError } from '@/common/util';
import { getAppCheckLimitedUseTokenHeaders, isFirebaseError } from '@/firebase';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';

export type SignupVariables = {
    /** From `useConfirmSignupVerificationAction` — proof the email is theirs. */
    token: string;
    firstName: string;
    lastName: string;
    password: string;
    /** One checkbox covers terms, privacy and the age declaration. */
    legal: boolean;
    marketing: boolean;
};

type UseSignupActionData = {
    signup: (variables: SignupVariables) => Promise<SignupResponse>;
    loading: boolean;
    error: string;
    /** Clears the message so a fresh attempt starts on a clean form. */
    clearError: () => void;
};

/**
 * Creating the account. Storing the tokens is what signs the new user in, so
 * nothing else has to happen for them to land in the app.
 */
export function useSignupAction(): UseSignupActionData {
    const { t } = useMeTranslation();
    const { setTokens } = useNullableTokens();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/auth/signup`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'INVALID_TOKEN')) {
                return t('actions.signup.error_invalid_token');
            }
            if (isApiError(error, 'USER_ALREADY_EXISTS')) {
                return t('shared.errors.email_already_exists');
            }
            if (isApiError(error, 'EMAIL_BLOCKED')) {
                return t('shared.errors.email_blocked');
            }
            if (isApiError(error, 'IP_BLOCKED')) {
                return t('shared.errors.ip_blocked');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({
            token,
            firstName,
            lastName,
            password,
            legal,
            marketing
        }: SignupVariables) =>
            // A limited-use token: signup follows the verification call, and a
            // replayed App Check token would fail the second.
            getAppCheckLimitedUseTokenHeaders()
                .then((headers) =>
                    openApiRef.current.auth.signup(
                        {
                            token,
                            firstName,
                            lastName,
                            password,
                            termsOfService: legal,
                            over16: legal,
                            privacyPolicy: legal,
                            contactOptIn: marketing
                        },
                        { headers }
                    )
                )
                .then((response) => response.data),
        onSuccess: (data) => {
            const { accessToken, refreshToken } = data.data;
            setTokens({ accessToken, refreshToken });

            gtmPush({ event: 'user_signup' });
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        signup: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : '',
        clearError: () => mutation.reset()
    };
}
